import { characterManager } from './character-manager.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { HangoutState } from '../state/HangoutState.js';
import { HangoutService } from '../services/HangoutService.js';
import { CDN_URL } from '../../config.js';
import { resolveLatestCharacterSpriteData } from '../shared/sprite-state.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import { getTimeDefinitionService } from '../../../core/services/TimeDefinitionService.js';
import {
    resolveScenarioBackdrop,
    shouldPreserveLocalBackdrop,
    shouldSeedScenarioBackdrop
} from '../shared/session-backdrop.js';

const VALID_TIME_OF_DAY = ['day', 'sunset', 'night'];

const sortCharactersByIdAsc = (characters) => {
    return [...characters].sort((a, b) => {
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        return idA - idB;
    });
};

const lastAssistantByCharacterId = (messages) => {
    const map = new Map();
    if (!messages?.length) return map;
    for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== 'assistant' || msg.characterId == null) continue;
        const id = String(msg.characterId);
        if (!map.has(id)) map.set(id, msg);
    }
    return map;
};

const isPlaceholderBackgroundUrl = (url) =>
    typeof url === 'string' && (/\/0\.\d+\.webp(?:$|\?)/.test(url) || /\/1\.\d+\.webp(?:$|\?)/.test(url));

const applyCanvasBackground = async (canvas, url) => {
    if (!canvas) return;
    let resolvedUrl = url;
    if (url && !isPlaceholderBackgroundUrl(url)) {
        try {
            resolvedUrl = await getBackgroundService().resolveExistingBackgroundFromUrl(url);
        } catch (error) {
            console.error('SessionManager: background image failed all variations:', url, error);
            throw error;
        }
    }
    canvas.classList.toggle('loading-background', !resolvedUrl);
    if (resolvedUrl) {
        canvas.style.setProperty('--bg-image', `url('${resolvedUrl}')`);
        return;
    }
    canvas.style.removeProperty('--bg-image');
};

const firstHttpUrl = async (sources) => {
    for (const source of sources) {
        const url = await source();
        if (typeof url === 'string' && url.includes('http')) return url;
    }
    return null;
};

class SessionManager {
    async initHangoutFromUrl() {
        const configFromStorageManager = await storageManager.getItem('currentHangoutConfig');
        const configFromStorage = storage.getHangoutInfo();
        let hangoutInfo = { character: null, location: null, scenario: null, type: null, description: null };

        if (configFromStorageManager?.allCharacters?.[0]?.id) {
            const { scenarioId, backgroundUrl, allCharacters } = configFromStorageManager;
            const characterId = allCharacters[0].id;
            const hangoutService = HangoutService.getInstance();
            const character = (await hangoutService.getAvailableCharacters())
                .find(c => c.id.toString() === characterId.toString());

            const configCharacter = allCharacters?.find(c => c.id.toString() === characterId.toString());
            const characterName = configCharacter?.name || character?.name || 'Unknown';

            hangoutInfo = {
                character: {
                    id: characterId,
                    name: characterName,
                    image: '/assets/images/kiss.webp'
                },
                location: {
                    id: scenarioId,
                    name: 'Unknown Spot',
                    description: 'No description',
                    spriteUrl: '/assets/images/kiss.webp'
                },
                scenario: scenarioId,
                type: 'regular',
                description: 'No description'
            };

            const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
            Object.assign(currentConfig, {
                characterId: String(characterId),
                scenarioId: String(scenarioId)
            });
            await storageManager.setItem('currentHangoutConfig', currentConfig);

            storage.setHangoutInfo(hangoutInfo);

            if (backgroundUrl) {
                await this.handlePromotionalBackground(backgroundUrl);
            } else if (scenarioId) {
                await this.handleRegularScenarioBackground(scenarioId);
            }

            await this.fetchFullManifestData(character, characterId, scenarioId, configFromStorageManager, hangoutInfo);
            return hangoutInfo;
        }

        if (configFromStorage?.character?.id) {
            configFromStorage.character.image = '/assets/images/kiss.webp';
            return configFromStorage;
        }

        hangoutInfo = {
            character: { id: '0', name: 'Unknown', image: '/assets/images/kiss.webp' },
            location: { id: '0', name: 'Unknown Spot', description: 'No description', spriteUrl: '/assets/images/kiss.webp' },
            scenario: '0',
            type: 'regular',
            description: 'No description'
        };
        storage.setHangoutInfo(hangoutInfo);
        return hangoutInfo;
    }

    async handlePromotionalBackground(backgroundUrl) {
        const hangoutState = HangoutState.getInstance();
        const validUrl = this.extractValidUrl(backgroundUrl);

        if (validUrl?.includes('http')) {
            await hangoutState.setSelectedBackground(validUrl);
        }
    }

    async handleRegularScenarioBackground(scenarioId) {
        const hangoutState = HangoutState.getInstance();
        const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};

        if (shouldPreserveLocalBackdrop(currentConfig)) {
            const timeOfDay = VALID_TIME_OF_DAY.includes(currentConfig.timeOfDay)
                ? currentConfig.timeOfDay
                : null;
            if (!currentConfig.loc_id || !timeOfDay) return;
            const backgroundUrl = getBackgroundService().constructBackgroundUrl(currentConfig.loc_id, timeOfDay);
            await hangoutState.setSelectedBackground(backgroundUrl);
            await applyCanvasBackground(document.getElementById('background-canvas'), backgroundUrl);
            return;
        }

        const hangoutService = HangoutService.getInstance();
        const scenario = await hangoutService.getScenarioDetails(scenarioId);
        const scenarioLoc = scenario?.loc_id ?? scenario?.loc ?? null;
        if (!scenarioLoc) return;

        const seedFromScenario = shouldSeedScenarioBackdrop(currentConfig);
        const resolved = seedFromScenario ? resolveScenarioBackdrop(scenario) : null;
        const existingTimeOfDay = currentConfig.timeOfDay;
        const hasValidExistingTimeOfDay = VALID_TIME_OF_DAY.includes(existingTimeOfDay);
        const scenarioTimeOfDay = resolved?.timeOfDay
            || getTimeDefinitionService().getTimeOfDayFromNumericSlot(scenario?.time || 3);
        const timeOfDay = seedFromScenario || !hasValidExistingTimeOfDay
            ? scenarioTimeOfDay
            : existingTimeOfDay;

        const locId = seedFromScenario && resolved?.locId ? resolved.locId : String(scenarioLoc);
        const backgroundUrl = getBackgroundService().constructBackgroundUrl(locId, timeOfDay);
        await hangoutState.setSelectedBackground(backgroundUrl);

        const configUpdates = {
            loc_id: String(locId),
            backgroundUrl,
            scenarioId: String(scenarioId)
        };
        if (seedFromScenario || !hasValidExistingTimeOfDay) {
            configUpdates.timeOfDay = timeOfDay;
        }
        if (seedFromScenario && resolved?.timeHour != null) {
            configUpdates.timeHour = resolved.timeHour;
        }
        Object.assign(currentConfig, configUpdates);
        await storageManager.setItem('currentHangoutConfig', currentConfig);

        await applyCanvasBackground(document.getElementById('background-canvas'), backgroundUrl);
    }

    async fetchFullManifestData(character, characterId, scenarioId, config, hangoutInfo) {
        try {
            if (!character) {
                console.error(`Character ${characterId} not found`);
                hangoutInfo.character.name = 'Unknown';
                hangoutInfo.location.name = 'Unknown Spot';
                storage.setHangoutInfo(hangoutInfo);
                return;
            }

            hangoutInfo.character.name = character.name;

            const hangoutState = HangoutState.getInstance();
            const hangoutService = HangoutService.getInstance();

            const scenarios = await hangoutService.getScenarios(characterId);
            await hangoutState.setScenarios(scenarios);

            let scenario = scenarios.find(s => s.id.toString() === scenarioId.toString()) ||
                config.selectedScenario;

            if (!scenario) {
                const hangoutConfig = storage.get('currentHangoutConfig') || {};
                if (hangoutConfig.isPrivateScenario) {
                    try {
                        scenario = await hangoutService.getScenarioDetails(scenarioId);
                    } catch (error) {
                        console.warn(`Failed to fetch private scenario ${scenarioId}:`, error);
                    }
                }
            }

            if (!scenario) {
                console.error(`Scenario ${scenarioId} not found in public or private scenarios`);
                Object.assign(hangoutInfo.location, {
                    name: 'Unknown Spot',
                    description: 'No description',
                    spriteUrl: '/assets/images/kiss.webp'
                });
                storage.setHangoutInfo(hangoutInfo);
                return;
            }

            const backgroundUrl = `${CDN_URL}/data/backgrounds_images/${scenario.loc || scenario.backgroundId}.1.webp`;

            Object.assign(hangoutInfo.location, {
                name: scenario.name,
                description: scenario.descr,
                spriteUrl: scenario.url,
                family: scenario.chars?.[0]?.family,
                id: scenario.loc || 0,
                backgroundUrl
            });

            await hangoutState.switchLocation({
                id: scenarioId,
                name: scenario.name,
                loc_id: scenario.loc || 0,
                backgroundUrl,
                timeOfDay: hangoutState.getCurrentTimeSlot()
            });

            await hangoutState.setSelectedScenario({
                id: scenario.id,
                name: scenario.name,
                description: scenario.descr,
                descr: scenario.descr,
                loc: scenario.loc,
                goal: scenario.goal || 'Engage in a conversation',
                characterGoals: { [characterId]: scenario.goal || 'Engage in a conversation' }
            });

            if (config.backgroundUrl && config.selectedScenario) {
                await this.handlePromotionalBackground(config.backgroundUrl);
            }

            storage.setHangoutInfo(hangoutInfo);
        } catch (error) {
            console.error('Failed to fetch manifest data:', error);
            hangoutInfo.character.name = 'Unknown';
            hangoutInfo.location.name = 'Unknown Spot';
            storage.setHangoutInfo(hangoutInfo);
        }
    }

    async applyInitialBackground() {
        const hangoutContainer = document.getElementById('hangout-container');
        hangoutContainer?.classList.add('is-ready');

        const backgroundCanvas = document.getElementById('background-canvas');
        if (!backgroundCanvas) return;

        const config = await storageManager.getItem('currentHangoutConfig');
        if (!config?.loc_id && !config?.selectedScenario?.loc_id && !config?.selectedScenario?.loc) {
            return;
        }

        const hangoutState = HangoutState.getInstance();
        const hangoutService = HangoutService.getInstance();
        const backgroundUrl = await this.getBackgroundUrl(config, hangoutState, hangoutService);

        if (!backgroundUrl) {
            await applyCanvasBackground(backgroundCanvas, null);
            return;
        }

        try {
            await applyCanvasBackground(backgroundCanvas, backgroundUrl);
        } catch (error) {
            console.error('SessionManager: failed to apply initial background:', error);
        }
    }

    async getBackgroundUrl(config, hangoutState, hangoutService) {
        const backgroundService = getBackgroundService();
        return firstHttpUrl([
            () => this.extractValidUrl(config?.backgroundUrl),
            async () => {
                if (config?.loc_id && config?.timeOfDay) {
                    return backgroundService.constructBackgroundUrl(config.loc_id, config.timeOfDay);
                }
                return config?.loc_id && hangoutService.getBackgroundUrl(config.loc_id);
            },
            async () => {
                if (!config?.scenarioId) return null;
                const scenario = await hangoutService.getScenarioDetails(config.scenarioId);
                const scenarioLoc = scenario?.loc_id ?? scenario?.loc ?? null;
                if (!scenarioLoc) return null;
                if (scenario.time) {
                    const timeOfDay = getTimeDefinitionService().getTimeOfDayFromNumericSlot(scenario.time);
                    return backgroundService.constructBackgroundUrl(scenarioLoc, timeOfDay);
                }
                return hangoutService.getBackgroundUrl(scenarioLoc);
            },
            () => this.extractValidUrl(hangoutState.getCurrentSession()?.backgroundUrl),
            async () => (await backgroundService.getCurrentBackground())?.backgroundUrl
        ]);
    }

    extractValidUrl(url) {
        if (typeof url === 'string' && url.includes('http')) return url;
        if (typeof url === 'object' && url) {
            return url.url || url.backgroundUrl || url.href || null;
        }
        const str = String(url);
        return str.includes('http') && !['[object Object]', 'undefined', 'null'].includes(str) ? str : null;
    }

    async initializeResumedVisuals() {
        const hangoutState = HangoutState.getInstance();
        const currentSession = hangoutState.getCurrentSession();
        const config = await storageManager.getItem('currentHangoutConfig');

        if (!currentSession || !config) return;

        const backgroundUrl = await this.getResumedBackgroundUrl(config, currentSession, hangoutState);
        if (backgroundUrl) {
            await this.syncAndApplyBackground(backgroundUrl, config);
        }

        const activeCharacters = config.allCharacters || [];
        if (activeCharacters.length > 1) {
            await this.initializeMultiCharacterSprites(config, hangoutState, currentSession);
            return;
        }
        if (activeCharacters.length === 1) {
            const { id, outfitId = '0', name = 'Character' } = activeCharacters[0];
            await this.initializeSingleCharacterSprite(id, outfitId, name, hangoutState, currentSession);
            return;
        }
        if (currentSession.characterId) {
            await this.initializeSingleCharacterSprite(
                currentSession.characterId,
                currentSession.outfitId || 0,
                'Character',
                hangoutState,
                currentSession
            );
        }
    }

    async getResumedBackgroundUrl(config, currentSession, hangoutState) {
        const backgroundService = getBackgroundService();
        return firstHttpUrl([
            () => config.backgroundUrl,
            async () => config.loc_id && config.loc_id !== '0' && await backgroundService.constructBackgroundUrl(config.loc_id, config.timeOfDay),
            () => currentSession.backgroundUrl,
            async () => {
                const metadata = storage.getConversationMetadata(currentSession.conversationId);
                return metadata?.backgroundUrl || ((metadata?.loc_id || metadata?.backgroundId) && await backgroundService.constructBackgroundUrl(metadata.loc_id || metadata.backgroundId));
            }
        ]);
    }

    async syncAndApplyBackground(backgroundUrl, config) {
        const hangoutState = HangoutState.getInstance();
        const match = backgroundUrl.match(/(\d+)\.\d+\.webp/);
        if (match) {
            await getBackgroundService().setBackground(match[1], config.timeOfDay || 'night');
        }

        await hangoutState.setSelectedBackground(backgroundUrl);
        try {
            await applyCanvasBackground(document.getElementById('background-canvas'), backgroundUrl);
        } catch (error) {
            console.error('SessionManager: failed to apply resumed background:', error);
        }
    }

    async initializeMultiCharacterSprites(config, hangoutState, currentSession) {
        const messages = hangoutState.getMessages();
        const allCharacters = config.allCharacters || [];
        const sortedCharacters = sortCharactersByIdAsc(allCharacters);
        const lastAssistant = lastAssistantByCharacterId(messages);
        const metadata = storage.getConversationMetadata(currentSession.conversationId);
        const storedConfig = storage.get('currentHangoutConfig') || {};

        await characterManager.cleanupLeftoverSprites();
        await Promise.allSettled(sortedCharacters.map(async (character) => {
            const { id: characterId, outfitId = '0', name: characterName = `Character ${character.id}` } = character;
            const displayIndex = allCharacters.findIndex((c) => String(c.id) === String(characterId));
            if (displayIndex < 0) return;

            const lastMsg = lastAssistant.get(String(characterId));
            const isVideo = lastMsg?.isVideo === true || lastMsg?.is_video === true;
            const spriteData = resolveLatestCharacterSpriteData({
                messages,
                characterId,
                metadata,
                config: storedConfig
            });

            try {
                const spriteUrl = await characterManager.fetchCharacterSprite(
                    characterId,
                    spriteData.actionId,
                    outfitId,
                    displayIndex,
                    true,
                    isVideo,
                    { allowEmotionFallback: false, skipLeftoverCleanup: true }
                );
                if (spriteUrl) {
                    await characterManager.updateCharacterImage(
                        spriteUrl,
                        characterName,
                        displayIndex,
                        spriteData.spriteState,
                        false,
                        isVideo,
                        { skipLeftoverCleanup: true }
                    );
                }
            } catch (error) {
                console.error(`Failed to fetch sprite for ${characterName}:`, error);
                await characterManager.updateCharacterImage('/assets/images/kiss.webp', characterName, displayIndex, null);
            }
        }));
    }

    async initializeSingleCharacterSprite(characterId, outfitId, characterName, hangoutState, currentSession) {
        const messages = hangoutState.getMessages();
        const spriteData = resolveLatestCharacterSpriteData({
            messages,
            characterId,
            metadata: storage.getConversationMetadata(currentSession.conversationId),
            config: storage.get('currentHangoutConfig') || {}
        });
        const lastMsg = lastAssistantByCharacterId(messages).get(String(characterId));
        const isVideo = lastMsg?.isVideo === true || lastMsg?.is_video === true;
        const spriteUrl = await characterManager.fetchCharacterSprite(
            characterId,
            spriteData.actionId,
            outfitId || 0,
            0,
            true,
            isVideo,
            { allowEmotionFallback: false }
        );
        if (spriteUrl) {
            await characterManager.updateCharacterImage(spriteUrl, characterName, 0, spriteData.spriteState, false, isVideo);
        }
    }

    async generateInitialGreeting(conversationId, scenarioId, characters, config) {
        window.isInitialGreetingStarting = true;
        const { generateInitialGreeting } = await import('./message-utils.js');
        await generateInitialGreeting(conversationId, scenarioId, characters, config);
    }
}

export const sessionManager = new SessionManager();
export const initHangoutFromUrl = () => sessionManager.initHangoutFromUrl();
export const applyInitialBackground = () => sessionManager.applyInitialBackground();
export const initializeResumedVisuals = () => sessionManager.initializeResumedVisuals();
